import React, { FC, PropsWithChildren, createContext, useMemo, useState, useCallback, useRef, useEffect } from 'react'

export type ISnackbarSeverity = 'error' | 'warning' | 'info' | 'success'

// Top is the default: the agent start screen and chat page dock their
// composer bottom-centre, where a bottom toast would cover it.
export type ISnackbarAnchor = 'top' | 'bottom'

export interface ISnackbarOptions {
  anchor?: ISnackbarAnchor,
  // Closes the toast after this many ms, unconditionally — no pause on
  // hover or window blur (unlike MUI Snackbar's own autoHideDuration).
  // Omit to keep MUI's default (paused-on-hover/blur) 6s behaviour.
  autoHideMs?: number,
}

export interface ISnackbarData {
  message: string,
  severity: ISnackbarSeverity,
  color: string,
  anchor: ISnackbarAnchor,
  autoHideMs?: number,
}

export interface ISnackbarContext {
  snackbar?: ISnackbarData,
  setSnackbar: {
    (message?: string | null, severity?: ISnackbarSeverity, options?: ISnackbarOptions): void,
  },
}

// Where the global snackbar sits. A top toast clears the fixed app bar
// (topBarHeightPx) by 8px rather than covering the nav.
export const snackbarPlacement = (anchor: ISnackbarAnchor, topBarHeightPx: number) => ({
  anchorOrigin: { vertical: anchor, horizontal: 'center' } as const,
  sx: anchor === 'top' ? { top: { xs: `${topBarHeightPx + 8}px` } } : undefined,
})

export const SNACKBAR_SEVERITY_COLORS: Record<ISnackbarSeverity, string> = {
  error: '#990000',
  warning: '#333399',
  info: '#000099',
  success: '#009900',
}

export const SnackbarContext = createContext<ISnackbarContext>({
  setSnackbar: () => {},
})

export const useSnackbarContext = (): ISnackbarContext => {
  const [ snackbar, setRawSnackbar ] = useState<ISnackbarData>()
  // Our own timer, independent of MUI Snackbar's autoHideDuration — that one
  // pauses on hover and on window blur, which is exactly wrong for a toast
  // that must always be gone within a fixed time (e.g. the login toast).
  const closeTimerRef = useRef<ReturnType<typeof setTimeout>>()

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = undefined
    }
  }, [])

  const setSnackbar = useCallback((message?: string | null, severity?: ISnackbarSeverity, options?: ISnackbarOptions) => {
    clearCloseTimer()
    if(!message) {
      setRawSnackbar(undefined)
    } else {
      const useSeverity = severity || 'info'
      const color = SNACKBAR_SEVERITY_COLORS[useSeverity]
      setRawSnackbar({
        message,
        severity: useSeverity,
        color,
        anchor: options?.anchor || 'top',
        autoHideMs: options?.autoHideMs,
      })
      if (options?.autoHideMs) {
        closeTimerRef.current = setTimeout(() => {
          setRawSnackbar(undefined)
        }, options.autoHideMs)
      }
    }

  }, [ clearCloseTimer ])

  // Cleanup on unmount only — a running timer that outlives its provider
  // would fire setState on an unmounted component.
  useEffect(() => clearCloseTimer, [ clearCloseTimer ])

  const contextValue = useMemo<ISnackbarContext>(() => ({
    snackbar,
    setSnackbar,
  }), [
    snackbar,
    setSnackbar,
  ])
  return contextValue
}

export const SnackbarContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const value = useSnackbarContext()
  return (
    <SnackbarContext.Provider value={ value }>
      { children }
    </SnackbarContext.Provider>
  )
}